import { AIErrorCode } from '../../types/ai';
import { AIMonitoringService } from '../aiMonitoringService';

/**
 * Enhanced error handler for AI service failures with comprehensive retry logic,
 * rate limiting handling, and graceful degradation
 */
export class AIServiceErrorHandler {
  private static readonly retryDelays = [1000, 2000, 4000];
  private static readonly rateLimitWindow = 60000;
  private static rateLimitTracker = new Map<string, { count: number; resetTime: number }>();

  /**
   * Handle AI operation with comprehensive error handling and fallback
   */
  static async handleAIFailure<T>(
    aiOperation: () => Promise<T>,
    fallbackOperation: () => T,
    context: string,
    options: {
      maxRetries?: number;
      enableRateLimit?: boolean;
      rateLimitKey?: string;
    } = {}
  ): Promise<T> {
    const { maxRetries = 3, enableRateLimit = true, rateLimitKey = context } = options;
    const startTime = Date.now();

    if (enableRateLimit && this.isRateLimited(rateLimitKey)) {
      AIMonitoringService.logRateLimit(context);
      AIMonitoringService.logFallbackUsage(context, 'Rate limited');
      return fallbackOperation();
    }

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const result = await aiOperation();
        
        const responseTime = Date.now() - startTime;
        AIMonitoringService.logSuccess(context, responseTime);
        
        if (enableRateLimit) {
          this.resetRateLimit(rateLimitKey);
        }
        
        return result;
      } catch (error) {
        const errorType = this.classifyError(error);
        
        if (errorType === 'RATE_LIMITED') {
          if (enableRateLimit) {
            this.trackRateLimit(rateLimitKey);
            AIMonitoringService.logRateLimit(context);
          }
          
          if (attempt < maxRetries - 1) {
            await this.delay(this.retryDelays[Math.min(attempt, this.retryDelays.length - 1)] * 2);
          }
        } else if (errorType === 'AUTHENTICATION_ERROR' || errorType === 'INVALID_INPUT') {
          break;
        } else if (attempt < maxRetries - 1) {
          await this.delay(this.retryDelays[Math.min(attempt, this.retryDelays.length - 1)]);
        }

        AIMonitoringService.logFailure({
          context,
          errorType,
          error: (error as any)?.message || 'Unknown error',
          attempt: attempt + 1,
          maxAttempts: maxRetries,
          timestamp: new Date().toISOString(),
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Server',
          stack: (error as any)?.stack,
          statusCode: (error as any)?.status || (error as any)?.response?.status
        });
      }
    }

    AIMonitoringService.logFallbackUsage(context, `All ${maxRetries} attempts failed`);
    return fallbackOperation();
  }

  /**
   * Classify error type for appropriate handling
   */
  static classifyError(error: any): AIErrorCode {
    const errorMessage = error?.message?.toLowerCase() || '';
    const statusCode = error?.status || error?.response?.status;

    if (statusCode === 429 || errorMessage.includes('rate limit') || errorMessage.includes('quota')) {
      return AIErrorCode.RATE_LIMITED;
    }
    
    if (statusCode === 401 || statusCode === 403 || errorMessage.includes('auth') || errorMessage.includes('key')) {
      return AIErrorCode.AUTHENTICATION_ERROR;
    }
    
    if (statusCode >= 400 && statusCode < 500) {
      return AIErrorCode.INVALID_INPUT;
    }
    
    if (errorMessage.includes('network') || errorMessage.includes('fetch') || error?.name === 'AbortError') {
      return AIErrorCode.NETWORK_ERROR;
    }
    
    if (statusCode >= 500 || errorMessage.includes('server') || errorMessage.includes('unavailable')) {
      return AIErrorCode.API_UNAVAILABLE;
    }

    return AIErrorCode.GENERATION_FAILED;
  }

  /**
   * Check if error is related to token limits
   */
  static isTokenLimitError(error: any): boolean {
    const errorMessage = error?.message?.toLowerCase() || '';
    const errorData = error?.data?.error?.toLowerCase() || '';
    const statusCode = error?.status || error?.response?.status;

    return (
      errorMessage.includes('token limit') ||
      errorMessage.includes('max_tokens') ||
      errorMessage.includes('context length') ||
      errorMessage.includes('too long') ||
      errorMessage.includes('maximum context') ||
      errorData.includes('token limit') ||
      errorData.includes('max_tokens') ||
      statusCode === 413 // Payload Too Large
    );
  }

  /**
   * Check if operation is rate limited
   */
  private static isRateLimited(key: string): boolean {
    const tracker = this.rateLimitTracker.get(key);
    if (!tracker) return false;

    const now = Date.now();
    if (now > tracker.resetTime) {
      this.rateLimitTracker.delete(key);
      return false;
    }

    return tracker.count >= 10;
  }

  /**
   * Track rate limit usage
   */
  private static trackRateLimit(key: string): void {
    const now = Date.now();
    const tracker = this.rateLimitTracker.get(key);

    if (!tracker || now > tracker.resetTime) {
      this.rateLimitTracker.set(key, {
        count: 1,
        resetTime: now + this.rateLimitWindow
      });
    } else {
      tracker.count++;
    }
  }

  /**
   * Reset rate limit counter
   */
  private static resetRateLimit(key: string): void {
    this.rateLimitTracker.delete(key);
  }

  /**
   * Delay utility for retry backoff
   */
  private static delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get user-friendly error message based on error type
   */
  static getUserFriendlyMessage(error: any, context: string): string {
    const errorType = this.classifyError(error);
    
    const contextMessages = {
      'idea-generation': {
        RATE_LIMITED: "You've generated a lot of ideas recently! Please wait a moment before generating more.",
        API_UNAVAILABLE: "Our AI idea generator is taking a break. Here are some curated ideas instead.",
        NETWORK_ERROR: "Connection issue detected. Check your internet and try again.",
        AUTHENTICATION_ERROR: "AI service authentication failed. Please contact support.",
        INVALID_INPUT: "Please check your skill and interest selections and try again.",
        GENERATION_FAILED: "We're having trouble generating ideas right now. Here are some curated suggestions based on your interests."
      },
      'validation-questions': {
        RATE_LIMITED: "Please wait a moment before generating more validation questions.",
        API_UNAVAILABLE: "AI validation generator is unavailable. Using standard validation questions.",
        NETWORK_ERROR: "Connection issue. Please check your internet and try again.",
        AUTHENTICATION_ERROR: "AI service authentication failed. Please contact support.",
        INVALID_INPUT: "Please check your idea details and try again.",
        GENERATION_FAILED: "Using standard validation questions for your idea."
      },
      'task-checklist': {
        RATE_LIMITED: "Please wait before generating another task checklist.",
        API_UNAVAILABLE: "AI task generator is unavailable. Using standard MVP checklist.",
        NETWORK_ERROR: "Connection issue. Please check your internet and try again.",
        AUTHENTICATION_ERROR: "AI service authentication failed. Please contact support.",
        INVALID_INPUT: "Please check your idea details and try again.",
        GENERATION_FAILED: "Using standard task checklist for your MVP."
      }
    };

    const messages = contextMessages[context as keyof typeof contextMessages] || contextMessages['idea-generation'];
    return (messages as any)[errorType] || messages.GENERATION_FAILED;
  }

  /**
   * Check if error should trigger fallback immediately (no retries)
   */
  static shouldSkipRetries(error: any): boolean {
    const errorType = this.classifyError(error);
    return errorType === 'AUTHENTICATION_ERROR' || errorType === 'INVALID_INPUT';
  }

  /**
   * Get retry delay based on attempt number and error type
   */
  static getRetryDelay(attempt: number, errorType: AIErrorCode): number {
    const baseDelay = this.retryDelays[Math.min(attempt, this.retryDelays.length - 1)];
    
    if (errorType === 'RATE_LIMITED') {
      return baseDelay * 3;
    }
    
    if (errorType === 'NETWORK_ERROR') {
      return baseDelay * 0.5;
    }
    
    return baseDelay;
  }
}

/**
 * Comprehensive user-facing error messages for AI service failures
 */
export const AIErrorMessages = {
  GENERATION_FAILED: "We're having trouble generating ideas right now. Here are some curated suggestions based on your interests.",
  API_UNAVAILABLE: "AI services are temporarily unavailable. You can still use manual research tools.",
  INVALID_INPUT: "Please check your selections and try again.",
  RATE_LIMITED: "You've reached the AI generation limit. Please try again in a few minutes.",
  NETWORK_ERROR: "Network connection issue. Please check your internet and try again.",
  AUTHENTICATION_ERROR: "AI service authentication failed. Please contact support.",

  contexts: {
    'idea-generation': {
      RATE_LIMITED: "You've generated a lot of ideas recently! Please wait a moment before generating more. In the meantime, try the manual research option.",
      API_UNAVAILABLE: "Our AI idea generator is taking a break. Here are some curated ideas based on your skills and interests instead.",
      NETWORK_ERROR: "Connection issue detected. Check your internet connection and try generating ideas again.",
      AUTHENTICATION_ERROR: "AI service authentication failed. Please refresh the page or contact support if the issue persists.",
      INVALID_INPUT: "Please make sure you've selected at least one skill, one interest, and a problem type before generating ideas.",
      GENERATION_FAILED: "We're having trouble generating personalized ideas right now. Here are some curated suggestions based on your interests."
    },
    'validation-questions': {
      RATE_LIMITED: "Please wait a moment before generating more validation questions. You can review the current questions in the meantime.",
      API_UNAVAILABLE: "AI validation generator is unavailable. We're using our proven validation question template instead.",
      NETWORK_ERROR: "Connection issue. Please check your internet and try generating validation questions again.",
      AUTHENTICATION_ERROR: "AI service authentication failed. Please refresh the page or contact support.",
      INVALID_INPUT: "Please make sure your idea details are complete before generating validation questions.",
      GENERATION_FAILED: "Using our standard validation questions for your idea. These have been tested with hundreds of founders."
    },
    'task-checklist': {
      RATE_LIMITED: "Please wait before generating another task checklist. You can review your current tasks in the meantime.",
      API_UNAVAILABLE: "AI task generator is unavailable. We're using our proven MVP development checklist instead.",
      NETWORK_ERROR: "Connection issue. Please check your internet and try generating the task list again.",
      AUTHENTICATION_ERROR: "AI service authentication failed. Please refresh the page or contact support.",
      INVALID_INPUT: "Please make sure your idea details are complete before generating a task checklist.",
      GENERATION_FAILED: "Using our standard task checklist for your MVP. This template has helped hundreds of makers build successful products."
    }
  },

  suggestions: {
    RATE_LIMITED: [
      "Try again in a few minutes",
      "Use manual research tools in the meantime",
      "Review your current results while waiting"
    ],
    API_UNAVAILABLE: [
      "Use the manual research option",
      "Try again in a few minutes",
      "Contact support if the issue persists"
    ],
    NETWORK_ERROR: [
      "Check your internet connection",
      "Try refreshing the page",
      "Switch to a different network if available"
    ],
    AUTHENTICATION_ERROR: [
      "Refresh the page",
      "Clear your browser cache",
      "Contact support if the issue continues"
    ],
    INVALID_INPUT: [
      "Double-check your selections",
      "Make sure all required fields are filled",
      "Try different combinations of skills and interests"
    ],
    GENERATION_FAILED: [
      "Try again in a few minutes",
      "Use the manual research option",
      "Contact support if problems persist"
    ]
  }
};

/**
 * Get user-friendly error message with context and recovery suggestions
 */
export function getAIErrorMessage(
  error: any, 
  context: string = 'general',
  includeRecovery: boolean = true
): { message: string; suggestions?: string[] } {
  const errorType = AIServiceErrorHandler.classifyError(error);
  
  const contextMessages = AIErrorMessages.contexts[context as keyof typeof AIErrorMessages.contexts];
  const message = (contextMessages as any)?.[errorType] || (AIErrorMessages as any)[errorType] || AIErrorMessages.GENERATION_FAILED;
  
  if (includeRecovery) {
    return {
      message,
      suggestions: (AIErrorMessages.suggestions as any)[errorType] || AIErrorMessages.suggestions.GENERATION_FAILED
    };
  }
  
  return { message };
}
